import React, { useCallback, useEffect, useState } from 'react';
import { Platform, ScrollView, useWindowDimensions } from 'react-native';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import CustomCarousel from '../carousel/CustomCustomCarousel';
import OnboardingOne from './pages/OnboardingOne';
import OnboardingTwo from './pages/OnboardingTwo';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hook';
import CustomCheckbox from '../common/CustomCheckbox';
import useTranslation from '../../hooks/translation-hook';
import { lightBoxActions } from '../../store/lightBoxSlice';
import OnboardingMobileOne from './pages/OnboardingMobileOne';
import OnboardingMobileTwo from './pages/OnboardingMobileTwo';
import OnboardingThree from './pages/OnboardingThree';
import OnboardingFour from './pages/OnboardingFour';
import OnboardingMobileThree from './pages/OnboardingMobileThree';
import OnboardingMobileFour from './pages/OnboardingMobileFour';
import Loading from '../common/Loading';
import { areOnboardingSliderAssetsReady, preloadOnboardingSliderAssets } from '../../helpers/popupAssetPreload';
import useCustomTheme from '../../hooks/theme-hook';
import { getOnboardingPalette } from './onboardingPalette';

export interface OnboardingProps {
  onClose?(dontShowAgain: boolean): void;
}

const Onboarding = (props: OnboardingProps) => {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const theme = useCustomTheme();
  const lightBoxStore = useAppSelector((selector) => selector.lightBox);

  const [checked, setChecked] = useState<boolean>(false);
  const [imagesReady, setImagesReady] = useState<boolean>(areOnboardingSliderAssetsReady());
  const [currentPage, setCurrentPage] = useState(0);
  const isDarkMode = !!theme.isDark;
  const backgroundColor = getOnboardingPalette(currentPage, isDarkMode).backgroundColor;
  const webPopupWidth = lightBoxStore.mobile ? 350 : 625;
  const popupMaxWidth = lightBoxStore.mobile ? 350 : 675;
  const shouldBlockForImages = Platform.OS !== 'web' && !imagesReady;
  const webNoDragStyle = Platform.OS === 'web' ? ({ userSelect: 'none' } as any) : undefined;
  const dontShowTextColor = isDarkMode ? '#ffffff' : '#000000';
  const dontShowCheckColor = '#000000';
  const dontShowBoxStyle = { backgroundColor: '#ffffff', borderColor: '#000000' };
  const footerPadding = layout.width > 1200 ? 16 : 12;
  const footerTopPadding = layout.width > 1200 ? 16 : 0;
  const footerHeight = 28 + footerTopPadding + footerPadding;
  const nativeBottomInset = Platform.OS === 'android' ? Math.max(insets.bottom, 0) : 0;
  const popupHeight = Math.min(600, Math.max(layout.height - 40 - nativeBottomInset, 0));
  const containerStyle = [
    { flexGrow: 0, height: popupHeight, width: Platform.OS === 'web' ? webPopupWidth : undefined },
    webNoDragStyle,
  ];

  const handleClose = useCallback(() => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
    props.onClose && props.onClose(checked);
    setChecked(false);
  }, [checked, dispatch, props.onClose]);

  useEffect(() => {
    dispatch(lightBoxActions.setOnClose(handleClose));
  }, [dispatch, handleClose]);

  useEffect(() => {
    let mounted = true;

    preloadOnboardingSliderAssets().then(() => {
      if (mounted) {
        setImagesReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const handleOnChange = (nextChecked: boolean) => {
    setChecked(nextChecked);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderFooterContent = (_currentPage?: number) => (
    <View
      style={{
        padding: footerPadding,
        paddingTop: footerTopPadding,
      }}
    >
      <CustomCheckbox
        value={'dontShowAgain'}
        label={t('Dont-show-this-again')}
        checked={checked}
        textStyle={{ color: dontShowTextColor }}
        color={dontShowCheckColor}
        boxStyle={dontShowBoxStyle}
        onChange={handleOnChange}
      />
    </View>
  );

  const renderFooterBackground = (pageIndex: number) => <View style={{ flex: 1, backgroundColor: getOnboardingPalette(pageIndex, isDarkMode).backgroundColor }} />;

  const content = (
    <View
      style={[
        {
          width: '100%',
          maxWidth: popupMaxWidth,
          height: '100%',
          backgroundColor,
        },
        webNoDragStyle,
      ]}
    >
      {!shouldBlockForImages ? (
        layout.width > 1200 ? (
          <CustomCarousel
            onPageChange={handlePageChange}
            onClose={handleClose}
            closeIconColor={isDarkMode ? '#fff' : undefined}
            renderFixedFooter={renderFooterContent}
            renderFooterBackground={renderFooterBackground}
            footerHeight={footerHeight}
            controlsBottomInset={footerHeight}
            bigView
          >
            <OnboardingOne />
            <OnboardingTwo />
            <OnboardingThree />
            <OnboardingFour />
          </CustomCarousel>
        ) : (
          <CustomCarousel
            onPageChange={handlePageChange}
            onClose={handleClose}
            closeIconColor={isDarkMode ? '#fff' : undefined}
            renderFixedFooter={renderFooterContent}
            renderFooterBackground={renderFooterBackground}
            footerHeight={footerHeight}
            controlsBottomInset={footerHeight}
            enableSwipe={Platform.OS !== 'web'}
          >
            <OnboardingMobileOne />
            <OnboardingMobileTwo />
            <OnboardingMobileThree />
            <OnboardingMobileFour />
          </CustomCarousel>
        )
      ) : (
        <View style={{ flexGrow: 1, backgroundColor }}>
          <View style={{ flexGrow: 1, minHeight: layout.width > 1200 ? 520 : 420, backgroundColor, position: 'relative' as any }}>
            <Loading style={{ backgroundColor: 'transparent' }} />
          </View>
          <View style={{ height: footerHeight, backgroundColor }}>
            {renderFooterContent()}
          </View>
        </View>
      )}
    </View>
  );

  if (Platform.OS !== 'web') {
    return <View style={containerStyle}>{content}</View>;
  }

  return (
    <ScrollView
      style={[containerStyle, { backgroundColor }]}
      contentContainerStyle={{ flexGrow: 1 }}
      nestedScrollEnabled={true}
      scrollEnabled={true}
      bounces={false}
      alwaysBounceVertical={false}
      alwaysBounceHorizontal={false}
      overScrollMode="never"
    >
      {content}
    </ScrollView>
  );
};

export default Onboarding;
